/**
 * Scribe Service
 *
 * API client for the AI Scribe backend service.
 * Handles audio upload, job status polling, and result retrieval.
 */

import axios from 'axios'
import { environment } from '../environments/environment'

const apiEndpoint = environment.webservices.endpoint

// Types
export type CallType = 'CC' | 'CF' | 'DI' | 'ME' | 'MB' | 'MI' | 'WC' | 'OC' | 'ET'

export type ScribeJobStatus =
  | 'pending'
  | 'transcribing'
  | 'diarizing'
  | 'summarizing'
  | 'completed'
  | 'failed'

export interface CallTypeInfo {
  code: CallType
  name: string
  description: string
}

export interface CreateJobRequest {
  audioFile: Blob
  patientId: string
  callType: CallType
  gender?: string
}

export interface CreateJobResponse {
  success: boolean
  job_id: string
  status: ScribeJobStatus
  message: string
  error?: string
}

export interface JobStatusResponse {
  success: boolean
  job_id: string
  status: ScribeJobStatus
  progress_percent: number
  current_step: string
  error_message?: string
  created_at?: string
  completed_at?: string
}

export interface SpeakerSegment {
  speaker: string
  start: number
  end: number
  text: string
}

export interface TranscriptData {
  raw: string
  diarized: string
  speaker_segments: SpeakerSegment[]
  duration_seconds?: number
  num_speakers?: number
}

export interface SummaryData {
  text: string
  sections: {
    hpi?: string
    interval_hx?: string
    pmhx?: string
    pshx?: string
    famhx?: string
    sochx?: string
    ros?: string
    assessment_plan?: string
    dx_workup?: string
  }
}

export interface JobResultResponse {
  success: boolean
  job_id: string
  status: ScribeJobStatus
  patient_id: string
  call_type: CallType
  call_type_display: string
  transcript: TranscriptData
  summary: SummaryData
  metadata: {
    model_version?: string
    backend_used?: string
    processing_time_seconds?: number
    tokens_generated?: number
    created_at?: string
    completed_at?: string
  }
  message?: string
  error?: string
}

export interface JobSummary {
  id: number
  job_id: string
  patient_id: number
  patient_ref_id: string
  call_type: CallType
  call_type_display: string
  status: ScribeJobStatus
  audio_duration_seconds?: number
  created_at?: string
  updated_at?: string
  completed_at?: string
  created_by?: string
  error_message?: string
}

export interface ListJobsResponse {
  success: boolean
  jobs: JobSummary[]
  total: number
  page: number
  limit: number
  total_pages: number
}

export interface CallTypesResponse {
  call_types: CallTypeInfo[]
}

class ScribeService {

  /**
   * Create a new scribe job with audio upload
   */
  async createJob(request: CreateJobRequest): Promise<CreateJobResponse> {
    const formData = new FormData()

    // Determine filename extension based on blob type
    let filename = 'recording.webm'
    if (request.audioFile.type.includes('mp4')) {
      filename = 'recording.mp4'
    } else if (request.audioFile.type.includes('mp3') || request.audioFile.type.includes('mpeg')) {
      filename = 'recording.mp3'
    }

    formData.append('audio_file', request.audioFile, filename)
    formData.append('patient_id', request.patientId)
    formData.append('call_type', request.callType)
    if (request.gender) {
      formData.append('gender', request.gender)
    }

    const response = await axios.post<CreateJobResponse>(
      `${apiEndpoint}/scribe/jobs`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 600000
      }
    )

    return response.data
  }

  /**
   * Get current status of a job
   */
  async getJobStatus(jobId: string): Promise<JobStatusResponse> {
    const response = await axios.get<JobStatusResponse>(
      `${apiEndpoint}/scribe/jobs/${jobId}/status`
    )
    return response.data
  }

  /**
   * Get full results for a completed job
   */
  async getJobResult(jobId: string): Promise<JobResultResponse> {
    const response = await axios.get<JobResultResponse>(
      `${apiEndpoint}/scribe/jobs/${jobId}/result`
    )
    return response.data
  }

  /**
   * List all scribe jobs for a patient
   */
  async listPatientJobs(
    patientId: string,
    options: { limit?: number; page?: number; status?: ScribeJobStatus } = {}
  ): Promise<ListJobsResponse> {
    const params = new URLSearchParams()
    if (options.limit) params.append('limit', options.limit.toString())
    if (options.page) params.append('page', options.page.toString())
    if (options.status) params.append('status', options.status)

    const response = await axios.get<ListJobsResponse>(
      `${apiEndpoint}/patients/${patientId}/scribe/jobs?${params.toString()}`
    )
    return response.data
  }

  /**
   * Delete a scribe job
   */
  async deleteJob(jobId: string): Promise<{ success: boolean; message: string }> {
    const response = await axios.delete(`${apiEndpoint}/scribe/jobs/${jobId}`)
    return response.data
  }

  /**
   * Get available call types
   */
  async getCallTypes(): Promise<CallTypeInfo[]> {
    const response = await axios.get<CallTypesResponse>(`${apiEndpoint}/scribe/call-types`)
    return response.data.call_types
  }

  /**
   * Reprocess an existing job with a new call type
   * This re-runs only the summarization step using the existing transcript
   */
  async reprocessJob(
    jobId: string,
    callType: CallType,
    gender?: string
  ): Promise<CreateJobResponse> {
    const response = await axios.post<CreateJobResponse>(
      `${apiEndpoint}/scribe/jobs/${jobId}/reprocess`,
      {
        call_type: callType,
        gender: gender
      }
    )
    return response.data
  }

  /**
   * Poll for job completion with callback
   */
  pollJobStatus(
    jobId: string,
    onStatusUpdate: (status: JobStatusResponse) => void,
    onComplete: (result: JobResultResponse) => void,
    onError: (error: Error) => void,
    intervalMs: number = 2000
  ): () => void {
    let cancelled = false

    const poll = async () => {
      if (cancelled) return

      try {
        const status = await this.getJobStatus(jobId)
        onStatusUpdate(status)

        if (status.status === 'completed') {
          const result = await this.getJobResult(jobId)
          onComplete(result)
          return
        }

        if (status.status === 'failed') {
          onError(new Error(status.error_message || 'Job processing failed'))
          return
        }

        // Continue polling if still processing
        if (!cancelled) {
          setTimeout(poll, intervalMs)
        }
      } catch (error: any) {
        if (!cancelled) {
          onError(error)
        }
      }
    }

    // Start polling
    poll()

    // Return cancel function
    return () => {
      cancelled = true
    }
  }

  /**
   * Export all scribe transcripts for a hospital
   * Returns a CSV file with all transcript data
   */
  async exportTranscripts(hospitalName?: string): Promise<Blob> {
    const params = new URLSearchParams()
    if (hospitalName) params.append('hospital_name', hospitalName)

    const response = await axios.get(
      `${apiEndpoint}/scribe/export?${params.toString()}`,
      { responseType: 'blob' }
    )
    return response.data
  }
}

// Export singleton instance
export const scribeService = new ScribeService()
export default ScribeService
